import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, HOME_ROUTE } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Icon, Btn } from "../components/ui-kit";
import { cn } from "../lib/utils";
import { toast } from "sonner";

const DEMO = [
  { role: "Student", email: "student@mim.com", pw: "Student@123", icon: "GraduationCap", tone: "blue" },
  { role: "Teacher", email: "teacher@mim.com", pw: "Teacher@123", icon: "Presentation", tone: "green" },
  { role: "School Admin", email: "schooladmin@mim.com", pw: "School@123", icon: "Building2", tone: "cyan" },
  { role: "Super Admin", email: "admin@mim.com", pw: "Admin@123", icon: "ShieldCheck", tone: "purple" },
  { role: "Parent", email: "parent@mim.com", pw: "Parent@123", icon: "Heart", tone: "coral" },
];

const COUNTRY_CODES = [
  { code: "+91", label: "India (+91)" },
  { code: "+1", label: "USA / Canada (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
  { code: "+971", label: "UAE (+971)" },
  { code: "+61", label: "Australia (+61)" },
  { code: "+65", label: "Singapore (+65)" },
  { code: "+60", label: "Malaysia (+60)" },
  { code: "+92", label: "Pakistan (+92)" },
  { code: "+880", label: "Bangladesh (+880)" },
  { code: "+94", label: "Sri Lanka (+94)" },
  { code: "+977", label: "Nepal (+977)" },
  { code: "+49", label: "Germany (+49)" },
  { code: "+33", label: "France (+33)" },
  { code: "+81", label: "Japan (+81)" },
  { code: "+86", label: "China (+86)" },
];

const inputCls = "mt-1.5 w-full px-4 py-2.5 rounded-xl border border-mim-line focus:border-mim-blue focus:ring-2 focus:ring-mim-blue/15 outline-none text-sm";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // sign in
  const [email, setEmail] = useState("student@mim.com");
  const [password, setPassword] = useState("Student@123");

  // create account
  const [form, setForm] = useState({ name: "", email: "", password: "", country_code: "+91", mobile: "", whatsapp: false });
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doLogin = async (e, em, pw) => {
    if (e) e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(em || email, pw || password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      navigate(HOME_ROUTE[user.role] || "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const validateSignup = () => {
    if (form.name.trim().length < 2) return "Please enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email address";
    if (form.password.length < 6) return "Password must be at least 6 characters";
    const digits = form.mobile.replace(/\D/g, "");
    if (digits.length < 6 || digits.length > 15) return "Please enter a valid mobile number";
    return null;
  };

  const doSignup = async (e) => {
    e.preventDefault();
    setError("");
    const v = validateSignup();
    if (v) { setError(v); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        country_code: form.country_code,
        mobile: form.mobile.replace(/\D/g, ""),
        whatsapp: form.whatsapp,
        role: "student",
      };
      const user = await register(payload);
      toast.success(`Welcome to Master in Minutes, ${user.name.split(" ")[0]}!`);
      navigate(HOME_ROUTE[user.role] || "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not create your account");
    } finally {
      setLoading(false);
    }
  };

  const toneBg = { blue: "bg-mim-soft text-mim-blue", green: "bg-mim-green/10 text-mim-green",
    cyan: "bg-mim-cyan/10 text-mim-cyan", purple: "bg-mim-purple/10 text-mim-purple", coral: "bg-mim-coral/10 text-mim-coral" };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left brand panel */}
      <div className="lg:w-[45%] bg-mim-navy relative overflow-hidden px-8 py-12 lg:p-14 flex flex-col justify-between text-white">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-mim-blue/20 blur-3xl" />
        <div className="absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-mim-purple/20 blur-3xl" />
        <div className="relative">
          <img src="/logo.png" alt="Master in Minutes" className="h-16 object-contain" />
        </div>
        <div className="relative">
          <div className="text-xs font-bold uppercase tracking-widest text-mim-cyan mb-4">AI-Native Learning</div>
          <h1 className="text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">Learn smarter.<br />Teach faster.<br /><span className="text-mim-cyan">Master in minutes.</span></h1>
          <p className="text-white/70 mt-5 max-w-md leading-relaxed">An AI tutor, content studio, assessment engine and student intelligence system — in one premium platform.</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {["Snapshot", "Flashcards", "Adaptive Quiz", "Ask AI", "Exam Radar"].map((f) => (
              <span key={f} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 border border-white/15">{f}</span>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/40">© 2026 Master in Minutes</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* Tabs */}
          <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-mim-soft border border-mim-line mb-6" data-testid="auth-tabs">
            {[["signin", "Sign in"], ["signup", "Create account"]].map(([id, label]) => (
              <button key={id} data-testid={`tab-${id}`} onClick={() => { setTab(id); setError(""); }}
                className={cn("py-2.5 rounded-xl text-sm font-semibold transition-all",
                  tab === id ? "bg-white text-mim-navy shadow-mim" : "text-mim-muted hover:text-mim-navy")}>
                {label}
              </button>
            ))}
          </div>

          {tab === "signin" ? (
            <>
              <h2 className="text-2xl font-bold text-mim-navy">Sign in to continue</h2>
              <p className="text-sm text-mim-muted mt-1">Pick a demo role below, or enter your credentials.</p>

              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-6">
                {DEMO.map((d) => (
                  <button key={d.role} data-testid={`demo-${d.role.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => { setEmail(d.email); setPassword(d.pw); doLogin(null, d.email, d.pw); }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-2xl border border-mim-line hover:border-mim-blue/40 hover:shadow-mim transition-all bg-white">
                    <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center", toneBg[d.tone])}>
                      <Icon name={d.icon} className="w-4 h-4" />
                    </div>
                    <span className="text-[10px] font-semibold text-mim-navy leading-tight text-center">{d.role}</span>
                  </button>
                ))}
              </div>

              <form onSubmit={doLogin} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-mim-navy">Email</label>
                  <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-mim-navy">Password</label>
                  <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
                </div>
                {error && <div className="text-sm text-mim-coral bg-mim-coral/10 rounded-xl px-4 py-2.5" data-testid="login-error">{error}</div>}
                <Btn type="submit" disabled={loading} className="w-full" iconRight="ArrowRight" data-testid="login-submit">
                  {loading ? "Signing in…" : "Sign in"}
                </Btn>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-mim-navy">Create your account</h2>
              <p className="text-sm text-mim-muted mt-1">Sign up in seconds and start learning right away.</p>

              <form onSubmit={doSignup} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-mim-navy">Full name</label>
                  <input data-testid="signup-name" type="text" placeholder="e.g. Aarav Gupta" value={form.name} onChange={(e) => setF("name", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-mim-navy">Email</label>
                  <input data-testid="signup-email" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => setF("email", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-mim-navy">Password</label>
                  <input data-testid="signup-password" type="password" placeholder="At least 6 characters" value={form.password} onChange={(e) => setF("password", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-mim-navy">Mobile number</label>
                  <div className="mt-1.5 flex gap-2">
                    <select data-testid="signup-country-code" value={form.country_code} onChange={(e) => setF("country_code", e.target.value)}
                      className="w-32 px-2.5 py-2.5 rounded-xl border border-mim-line focus:border-mim-blue focus:ring-2 focus:ring-mim-blue/15 outline-none text-sm bg-white">
                      {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    <input data-testid="signup-mobile" type="tel" inputMode="numeric" placeholder="Mobile number" value={form.mobile}
                      onChange={(e) => setF("mobile", e.target.value.replace(/[^\d\s-]/g, ""))}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-mim-line focus:border-mim-blue focus:ring-2 focus:ring-mim-blue/15 outline-none text-sm" />
                  </div>
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="signup-whatsapp-label">
                  <input data-testid="signup-whatsapp" type="checkbox" checked={form.whatsapp} onChange={(e) => setF("whatsapp", e.target.checked)}
                    className="w-4 h-4 rounded border-mim-line text-mim-green focus:ring-mim-green/30 accent-mim-green" />
                  <span className="text-sm text-mim-navy inline-flex items-center gap-1.5"><Icon name="MessageCircle" className="w-4 h-4 text-mim-green" />This number is also on WhatsApp</span>
                </label>
                {error && <div className="text-sm text-mim-coral bg-mim-coral/10 rounded-xl px-4 py-2.5" data-testid="signup-error">{error}</div>}
                <Btn type="submit" disabled={loading} className="w-full" iconRight="ArrowRight" data-testid="signup-submit">
                  {loading ? "Creating account…" : "Create account & continue"}
                </Btn>
                <p className="text-[11px] text-mim-muted text-center">By creating an account you agree to our terms. Email OTP verification will be added soon.</p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
